import { ChevronNav } from "@theme/assets";

import { paginationTrackStart } from "@theme/components/Pagination/utils";
import { Box, Flex, IconButton, Text } from "@theme/components/ThemeUi";

import { clamp } from "@theme/utils";

import { sentenceToCamelCase } from "@theme/utils/strings";

import type { FC } from "react";
import type { ThemeUIStyleObject } from "theme-ui";

export type PaginationColor = "Brand" | "Neutral Primary" | "Neutral Level 2";

export interface DotPaginationProps {
  /** Accessible label for the nav region */
  ariaLabel?: string;
  /** Accessible label for the next button */
  ariaLabelNext?: string;
  /** Accessible label for the previous button */
  ariaLabelPrevious?: string;
  /** Can scroll to next page override */
  canScrollNextOverride?: boolean;
  /** Can scroll to previous page override */
  canScrollPrevOverride?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** The color variant used */
  color?: PaginationColor;
  /** Current active index */
  currentIndex: number;
  /** Data-testid attribute for testing */
  dataTestId?: string;
  /** Number of dots visible at once */
  dotsToShow?: number;
  /** Callback for when the next button is clicked */
  onNextClick: () => void;
  /** Callback for when the previous button is clicked */
  onPreviousClick: () => void;
  /** Callout for screen readers to know element type. Default: 'Page' */
  pageLabel?: string;
  /** Total number of dots */
  totalDots: number;
  /** Additional sx styles */
  sx?: ThemeUIStyleObject;
}

const DOT_SIZE = 4;
const GAP = 6;
const SVG_THEME = {
  "&&&": {
    color: "currentColor",
    path: {
      strokeWidth: 2,
    },
  },
};

export const DotPagination: FC<DotPaginationProps> = ({
  ariaLabel = "Pagination",
  ariaLabelNext = "Go to next",
  ariaLabelPrevious = "Go to previous",
  canScrollNextOverride,
  canScrollPrevOverride,
  className,
  color = "Brand",
  currentIndex,
  dataTestId,
  dotsToShow = 5,
  onNextClick,
  onPreviousClick,
  pageLabel,
  totalDots,
  sx,
}) => {
  const isBrand = color === "Brand";
  const adjustedTotal = Math.max(1, totalDots); // Min total of 1
  const current = clamp(currentIndex + 1, 1, adjustedTotal);
  const visibleDots = clamp(adjustedTotal, 1, dotsToShow); // Adjusted number of visible dots

  const startIndex = paginationTrackStart(current - 1, adjustedTotal, visibleDots);
  const endIndex = startIndex + visibleDots - 1;

  const step = DOT_SIZE + GAP;
  const offsetPx = startIndex * step;
  const containerWidth = visibleDots * DOT_SIZE + (visibleDots - 1) * GAP;

  const canGoPrev =
    typeof canScrollPrevOverride === "boolean" ? canScrollPrevOverride : current > 1;
  const canGoNext =
    typeof canScrollNextOverride === "boolean" ? canScrollNextOverride : current < adjustedTotal;

  const prev = () => canGoPrev && onPreviousClick();
  const next = () => canGoNext && onNextClick();

  return (
    <Flex
      as="nav"
      aria-label={ariaLabel || "Pagination"}
      className={className}
      data-testid={dataTestId}
      sx={{
        variant: `pagination.${sentenceToCamelCase(color)}`,
        ...sx,
      }}
    >
      {/* SR-only live region to announce changes */}
      <Text aria-live="polite" variant="screenReaderOnly">
        {`${pageLabel || "Page"} ${current} of ${adjustedTotal}`}
      </Text>

      <IconButton
        onClick={prev}
        aria-label={ariaLabelPrevious}
        data-testid={dataTestId ? `${dataTestId}-previous` : "pagination-previous"}
        disabled={!canGoPrev}
        aria-disabled={!canGoPrev}
        variant={isBrand ? "iconPaginationBrand" : "iconPaginationNeutral"}
      >
        <ChevronNav sx={{ ...SVG_THEME, transform: "rotate(90deg)" }} />
      </IconButton>
      <Box
        style={{
          width: containerWidth,
          overflow: "hidden",
        }}
      >
        <Flex
          as="ul"
          aria-hidden="true"
          role="presentation"
          data-id="pagination-track"
          sx={{
            gap: GAP,
            transform: `translateX(${-offsetPx}px)`,
          }}
        >
          {Array.from({ length: adjustedTotal }, (_, i) => {
            const isActive = currentIndex === i;
            const reducedSize = !isActive && (i === startIndex || i === endIndex);

            return (
              <Flex
                as="li"
                key={i}
                data-id="pagination-dot"
                data-active={isActive}
                data-reduced={reducedSize}
                sx={{
                  ":after": {
                    width: DOT_SIZE,
                    minWidth: DOT_SIZE,
                    height: DOT_SIZE,
                  },
                }}
              />
            );
          })}
        </Flex>
      </Box>

      <IconButton
        onClick={next}
        aria-label={ariaLabelNext}
        data-testid={dataTestId ? `${dataTestId}-next` : "pagination-next"}
        disabled={!canGoNext}
        aria-disabled={!canGoNext}
        variant={isBrand ? "iconPaginationBrand" : "iconPaginationNeutral"}
      >
        <ChevronNav sx={{ ...SVG_THEME, transform: "rotate(270deg)" }} />
      </IconButton>
    </Flex>
  );
};
